import { Button, Icon, KpiCard, Sparkline, type NavId } from '../components'
import { useActiveOrg } from '../app-state/active-org'
import { useOrganizations } from '../lib/api/organizations'
import { useCashflow } from '../lib/api/reports'
import { useInvoiceReceivableSummary } from '../lib/api/invoices'
import { useLicenseDebtSummary } from '../lib/api/licenses'
import { useSalaryDebtSummary } from '../lib/api/salaries'
import { useForecast } from '../lib/api/forecast'
import { useAcknowledgeAlert, useAlerts, type Alert } from '../lib/api/alerts'
import { fmtMoney, fmtMoneyShort } from '../lib/money'
import { T } from '../data/mock'
import { useMe } from '../lib/use-me'
import { canViewExecutive } from '../lib/permissions'

type Props = {
  onNavigate: (id: NavId) => void
  // Kept on the contract for the toolbar wiring; the dashboard no longer renders
  // quick actions or the per-account balances widget (the sidebar rail owns both).
  onAction: (id: 'new-tx') => void
  onSelectAccount?: (id: string) => void
}

const uah0 = new Intl.NumberFormat('uk-UA', { maximumFractionDigits: 0 })
// 'YYYY-MM-DD' → 'dd.mm' for compact week/period labels.
const dayLabel = (iso: string) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}` : '')

// The common shape of the licence- and salary-debt summaries (the fields the
// aging card renders). Both API types are structurally assignable to it.
type DebtLike = {
  debtUah: number
  paidUah: number
  overdueUah: number
  overdueCount: number
  buckets: { within: number; uah: number; count: number }[]
  laterUah: number
  fxIncomplete: boolean
}

// Vendor-debt / salary-debt aging card: paid-vs-owed progress + an aging ladder
// (overdue / ≤7 / ≤14 / ≤30 / later). Shared so both blocks stay identical.
function DebtCard({
  title,
  paidLabel,
  emptyText,
  loading,
  data,
  onOpen,
}: {
  title: string
  paidLabel: string
  emptyText: string
  loading: boolean
  data: DebtLike | undefined
  onOpen: () => void
}) {
  const d = data
  const overdue = (d?.overdueCount ?? 0) > 0
  const bucket = (within: number) => d?.buckets.find((b) => b.within === within)
  const tiles = [
    { label: 'Прострочено', uah: d?.overdueUah ?? 0, count: d?.overdueCount, danger: true },
    { label: 'до 7 днів', uah: bucket(7)?.uah ?? 0, count: bucket(7)?.count, danger: false },
    { label: 'до 14 днів', uah: bucket(14)?.uah ?? 0, count: bucket(14)?.count, danger: false },
    { label: 'до 30 днів', uah: bucket(30)?.uah ?? 0, count: bucket(30)?.count, danger: false },
    { label: 'пізніше', uah: d?.laterUah ?? 0, count: undefined, danger: false },
  ]
  const paidPct = d && d.debtUah > 0 ? Math.min(100, Math.max(0, (d.paidUah / d.debtUah) * 100)) : 0

  return (
    <div className="card">
      <div className="card-head">
        <h3>{title}</h3>
        {overdue && (
          <span className="chip danger" style={{ marginLeft: 4 }}>
            прострочено {d!.overdueCount}
          </span>
        )}
        <div style={{ flex: 1 }} />
        <Button variant="ghost" size="sm" onClick={onOpen}>
          {T.buttons.open}
          <Icon name="arrow-right" size={12} />
        </Button>
      </div>
      <div
        className="card-body"
        style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        {loading ? (
          <div style={{ color: 'var(--fg-3)', fontSize: 13 }}>Завантаження…</div>
        ) : !d || d.debtUah === 0 ? (
          <div style={{ color: 'var(--fg-3)', fontSize: 13 }}>{emptyText}</div>
        ) : (
          <>
            <div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 12,
                  color: 'var(--fg-2)',
                  marginBottom: 6,
                }}
              >
                <span>{paidLabel}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {fmtMoneyShort(d.paidUah, 'UAH')} / {fmtMoneyShort(d.debtUah, 'UAH')}
                </span>
              </div>
              <div
                style={{ height: 6, borderRadius: 3, background: 'var(--bg-3)', overflow: 'hidden' }}
              >
                <div style={{ height: '100%', width: `${paidPct}%`, background: 'var(--accent)' }} />
              </div>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                gap: 8,
              }}
            >
              {tiles.map((t) => (
                <div
                  key={t.label}
                  style={{
                    background: 'var(--bg-3)',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: 6,
                    padding: '10px 12px',
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      color: 'var(--fg-4)',
                    }}
                  >
                    {t.label}
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: t.danger && t.uah > 0 ? 'var(--danger-fg)' : 'var(--fg-1)',
                      fontVariantNumeric: 'tabular-nums',
                      marginTop: 4,
                    }}
                  >
                    {fmtMoneyShort(t.uah, 'UAH')}
                  </div>
                  {t.count != null && (
                    <div style={{ fontSize: 10, color: 'var(--fg-4)', marginTop: 2 }}>
                      {t.count} шт.
                    </div>
                  )}
                </div>
              ))}
            </div>
            {d.fxIncomplete && (
              <div style={{ fontSize: 11, color: 'var(--fg-4)' }}>
                Частина без курсу — суми в UAH неповні.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function AlertRow({
  alert,
  onAck,
  disabled,
}: {
  alert: Alert
  onAck: () => void
  disabled: boolean
}) {
  const p = alert.payload
  const dot = alert.severity === 'critical' ? 'danger' : 'warning'
  return (
    <div className="alert">
      <span className={`alert-dot ${dot}`} />
      <div className="alert-text">
        <div className="alert-title">Касовий розрив · тиждень {dayLabel(p.weekStart)}</div>
        <div className="alert-body">
          Проєкційний баланс {uah0.format(p.projectedClose)} UAH (поріг {uah0.format(p.threshold)}{' '}
          UAH).
        </div>
      </div>
      <button
        type="button"
        onClick={onAck}
        disabled={disabled}
        aria-label="Закрити сповіщення"
        style={{
          background: 'none',
          border: 'none',
          cursor: disabled ? 'default' : 'pointer',
          color: 'var(--fg-4)',
          fontSize: 14,
          lineHeight: 1,
          opacity: disabled ? 0.5 : 1,
          padding: '2px 4px',
          alignSelf: 'flex-start',
        }}
      >
        ✕
      </button>
    </div>
  )
}

export function Dashboard({ onNavigate }: Props) {
  // ---- Role capability flag ----
  const me = useMe()
  const showExecutive = canViewExecutive(me.data?.role)

  // ---- Period bounds for the month-to-date cashflow KPI ----
  const now = new Date()
  const yyyy = now.getFullYear()
  const mm = String(now.getMonth() + 1).padStart(2, '0')
  const dd = String(now.getDate()).padStart(2, '0')
  const monthStart = `${yyyy}-${mm}-01`
  const today = `${yyyy}-${mm}-${dd}`
  const monthKey = `${yyyy}-${mm}`
  const monthName = new Intl.DateTimeFormat('uk-UA', { month: 'long' }).format(now)

  // ---- KPI row (global, all organisations) ----
  const cashflow = useCashflow({
    dateFrom: monthStart,
    dateTo: today,
    period: 'month',
    currencyMode: 'base',
  }, { enabled: showExecutive })
  const netRaw = cashflow.data?.totals.net[monthKey]
  const net = typeof netRaw === 'number' ? netRaw : 0
  // Components behind the net, shown small under the headline figure. In base
  // mode income is positive and expense negative (net = income + expense).
  const incRaw = cashflow.data?.totals.income[monthKey]
  const income = typeof incRaw === 'number' ? incRaw : 0
  const expRaw = cashflow.data?.totals.expense[monthKey]
  const expense = typeof expRaw === 'number' ? expRaw : 0

  const receivable = useInvoiceReceivableSummary()
  const r = receivable.data
  const rOverdue = (r?.overdueCount ?? 0) > 0

  const debt = useLicenseDebtSummary({}, { enabled: showExecutive })
  const d = debt.data
  const dOverdue = (d?.overdueCount ?? 0) > 0

  const salary = useSalaryDebtSummary()
  const s = salary.data
  const sOverdue = (s?.overdueCount ?? 0) > 0

  // ---- Forecast (per active organisation, like the Forecast screen) ----
  const { activeOrgId } = useActiveOrg()
  const orgs = useOrganizations()
  const selectedOrgId: string | null = activeOrgId ?? orgs.data?.[0]?.id ?? null
  const activeOrg = orgs.data?.find((o) => o.id === selectedOrgId) ?? null
  const forecast = useForecast({
    organizationId: selectedOrgId ?? '',
    weeks: 8,
    enabled: showExecutive && !!selectedOrgId,
  })
  const fweeks = forecast.data?.weeks ?? []
  const sparkPoints = fweeks.map((w) => w.close)
  const gapIdx = fweeks.findIndex((w) => w.flags.belowThreshold)
  const gapCount = fweeks.filter((w) => w.flags.belowThreshold).length
  const threshold = forecast.data?.threshold ?? 0
  const minClose = fweeks.length ? Math.min(...sparkPoints) : 0
  const nextWeek = fweeks[0]

  // ---- Alerts ----
  const alerts = useAlerts({ status: 'open' })
  const ack = useAcknowledgeAlert()
  const alertItems = alerts.data?.items ?? []

  return (
    <div className="content">
      {/* KPI row — month cashflow, receivable, vendor debt, salary debt */}
      <div className="metric-grid" style={{ gap: 12, marginBottom: 16 }}>
        {showExecutive && (
          <KpiCard
            eyebrow={T.dash.cashflow_mtd}
            onClick={() => onNavigate('cashflow')}
            value={
              cashflow.isLoading ? (
                '…'
              ) : (
                <span
                  style={{
                    color:
                      net < 0 ? 'var(--danger-fg)' : net > 0 ? 'var(--success-fg)' : 'var(--fg-1)',
                  }}
                >
                  {fmtMoney(net, 'UAH', { sign: true })}
                </span>
              )
            }
            sub={
              cashflow.isLoading ? undefined : (
                <span>
                  <span style={{ color: 'var(--success-fg)' }}>+{fmtMoneyShort(income, 'UAH')}</span>
                  {'  ·  '}
                  <span style={{ color: 'var(--danger-fg)' }}>
                    −{fmtMoneyShort(Math.abs(expense), 'UAH')}
                  </span>
                </span>
              )
            }
            meta={`за ${monthName}`}
          />
        )}
        <KpiCard
          eyebrow="Дебіторка"
          onClick={() => onNavigate('invoices')}
          value={receivable.isLoading ? '…' : fmtMoneyShort(r?.receivableUah ?? 0, 'UAH')}
          sub="нам винні клієнти"
          meta={
            rOverdue
              ? `прострочено ${r!.overdueCount} · ${fmtMoneyShort(r!.overdueUah, 'UAH')}`
              : T.dash.invoices_outstanding(r?.count ?? 0)
          }
          metaTone={rOverdue ? 'danger' : undefined}
        />
        {showExecutive && (
          <KpiCard
            eyebrow="Борг вендору"
            onClick={() => onNavigate('licenses')}
            value={debt.isLoading ? '…' : fmtMoneyShort(d?.remainingUah ?? 0, 'UAH')}
            sub="наш борг за ліцензії"
            meta={
              dOverdue
                ? `прострочено ${d!.overdueCount} · ${fmtMoneyShort(d!.overdueUah, 'UAH')}`
                : `${d?.count ?? 0} ліцензій`
            }
            metaTone={dOverdue ? 'danger' : undefined}
          />
        )}
        <KpiCard
          eyebrow="Зарплата"
          onClick={() => onNavigate('salaries')}
          value={salary.isLoading ? '…' : fmtMoneyShort(s?.remainingUah ?? 0, 'UAH')}
          sub="наш борг працівникам"
          meta={
            sOverdue
              ? `прострочено ${s!.overdueCount} · ${fmtMoneyShort(s!.overdueUah, 'UAH')}`
              : `${s?.count ?? 0} нарахувань`
          }
          metaTone={sOverdue ? 'danger' : undefined}
        />
      </div>

      {/* Main widgets — forecast + salary */}
      <div className="split-main" style={{ gap: 12, marginBottom: 16 }}>
        {showExecutive && <div className="card">
          <div className="card-head">
            <h3>Прогноз грошового потоку</h3>
            {activeOrg && (
              <span style={{ color: 'var(--fg-3)', fontSize: 11 }}>{activeOrg.name}</span>
            )}
            <div style={{ flex: 1 }} />
            {gapCount > 0 && <span className="chip warning">{gapCount} тижн. з розривом</span>}
            <Button variant="ghost" size="sm" onClick={() => onNavigate('forecast')}>
              {T.buttons.open}
              <Icon name="arrow-right" size={12} />
            </Button>
          </div>
          <div className="card-body" style={{ padding: 20, paddingTop: 8 }}>
            {!selectedOrgId ? (
              <p style={{ color: 'var(--fg-3)', fontSize: 13, margin: 0 }}>
                Оберіть організацію для перегляду прогнозу.
              </p>
            ) : forecast.isLoading ? (
              <p style={{ color: 'var(--fg-3)', fontSize: 13, margin: 0 }}>Завантаження…</p>
            ) : sparkPoints.length === 0 ? (
              <p style={{ color: 'var(--fg-3)', fontSize: 13, margin: 0 }}>
                Немає даних для прогнозу.
              </p>
            ) : (
              <>
                <Sparkline
                  points={sparkPoints}
                  width={520}
                  height={72}
                  gapIdx={gapIdx >= 0 ? gapIdx : undefined}
                />
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: 8,
                    fontSize: 10,
                    color: 'var(--fg-4)',
                  }}
                >
                  {fweeks.map((w) => (
                    <span key={w.weekId}>{dayLabel(w.weekStart)}</span>
                  ))}
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px 20px',
                    marginTop: 14,
                    fontSize: 12,
                    color: 'var(--fg-3)',
                  }}
                >
                  <span>
                    Мін. баланс:{' '}
                    <strong
                      style={{
                        color: minClose < threshold ? 'var(--danger-fg)' : 'var(--fg-1)',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {fmtMoneyShort(minClose, 'UAH')}
                    </strong>
                  </span>
                  <span>
                    Поріг:{' '}
                    <strong style={{ color: 'var(--fg-1)', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtMoneyShort(threshold, 'UAH')}
                    </strong>
                  </span>
                </div>
                {nextWeek && (
                  <div style={{ marginTop: 8, fontSize: 11, color: 'var(--fg-4)' }}>
                    Найближчий тиждень {dayLabel(nextWeek.weekStart)}–{dayLabel(nextWeek.weekEnd)}:
                    початок {uah0.format(nextWeek.opening)} ·{' '}
                    <span style={{ color: 'var(--success-fg)' }}>
                      +{uah0.format(nextWeek.income)}
                    </span>{' '}
                    ·{' '}
                    <span style={{ color: 'var(--danger-fg)' }}>
                      −{uah0.format(Math.abs(nextWeek.expense))}
                    </span>{' '}
                    · кінець {uah0.format(nextWeek.close)} UAH
                  </div>
                )}
              </>
            )}
          </div>
        </div>}

        <DebtCard
          title="Зарплата — борг"
          paidLabel="Сплачено працівникам"
          emptyText="Немає непогашеної зарплати."
          loading={salary.isLoading}
          data={s}
          onOpen={() => onNavigate('salaries')}
        />
      </div>

      {/* Secondary — licences + alerts */}
      <div className="split-main" style={{ gap: 12, marginBottom: 16 }}>
        {showExecutive && (
          <DebtCard
            title="Ліцензії — борг вендору"
            paidLabel="Сплачено вендору"
            emptyText="Немає непогашеного боргу за ліцензіями."
            loading={debt.isLoading}
            data={d}
            onOpen={() => onNavigate('licenses')}
          />
        )}

        <div className="card">
          <div className="card-head">
            <h3>{T.dash.alerts}</h3>
            {alertItems.length > 0 && (
              <span className="chip danger" style={{ marginLeft: 4 }}>
                {alertItems.length}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {alerts.isLoading ? (
              <div style={{ padding: 14, color: 'var(--fg-3)', fontSize: 13 }}>Завантаження…</div>
            ) : alertItems.length === 0 ? (
              <div style={{ padding: 14, color: 'var(--fg-3)', fontSize: 13 }}>
                Немає активних сповіщень.
              </div>
            ) : (
              alertItems.map((a) => (
                <AlertRow
                  key={a.id}
                  alert={a}
                  onAck={() => ack.mutate(a.id)}
                  disabled={ack.isPending}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
